import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit2, Trash2, ChevronUp, ChevronDown, X, Save, Eye, Lock, Upload, Loader2, Mic, Trash, Headphones, Sparkles, RefreshCw } from 'lucide-react';
import AiGenerateButton from '@/components/ai/AiGenerateButton';

const EMPTY_LESSON = {
  order: 1, slug: '', title: '', subtitle: '', body: '',
  key_takeaways: [], reflection_prompt: '', exercise: null, quiz: [],
  is_free_preview: false, audio_url: '', audio_filename: '',
  cinematic_mood: 'focus', cinematic_art_url: '', cinematic_soundtrack_url: '',
};

const CINEMATIC_MOOD_OPTIONS = [
  { value: 'focus',  label: 'Focus — sharp, monastic',         swatch: 'from-zinc-800 to-amber-400' },
  { value: 'calm',   label: 'Calm — midnight, moonlight',       swatch: 'from-slate-900 to-blue-300' },
  { value: 'grit',   label: 'Grit — embers, weathered iron',    swatch: 'from-black to-orange-500' },
  { value: 'hope',   label: 'Hope — golden horizon',            swatch: 'from-black to-amber-300' },
  { value: 'shadow', label: 'Shadow — chiaroscuro, gold rim',   swatch: 'from-black to-yellow-700' },
  { value: 'storm',  label: 'Storm — kinetic, electric',        swatch: 'from-zinc-900 to-amber-500' },
];

export default function LessonManager({ course, onBack }) {
  const [lessons, setLessons] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/admin/courses/${course.id}/lessons`);
    setLessons(data || []);
  };
  useEffect(() => { load(); }, [course.id]);

  const newLesson = () => {
    const maxOrder = lessons.reduce((m, l) => Math.max(m, l.order), 0);
    setEditing({ ...EMPTY_LESSON, course_id: course.id, order: maxOrder + 1 });
  };

  const del = async (l) => {
    if (!window.confirm(`Delete lesson "${l.title}"?`)) return;
    await api.delete(`/admin/lessons/${l.id}`);
    load();
  };

  const move = async (l, direction) => {
    await api.post(`/admin/lessons/${l.id}/reorder?direction=${direction}`);
    load();
  };

  return (
    <div data-testid="admin-lessons">
      <button onClick={onBack} className="text-sm text-muted-kc hover:text-ink mb-4 inline-flex items-center gap-1" data-testid="lm-back"><ArrowLeft size={14}/> All courses</button>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.18em] uppercase text-gold">{course.subtitle || course.slug}</div>
          <div className="font-display uppercase text-3xl mt-1">{course.title}</div>
          <div className="text-sm text-muted-kc mt-1">{lessons.length} lessons</div>
        </div>
        <button onClick={newLesson} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="lm-new-lesson">
          <Plus size={16}/> New Lesson
        </button>
      </div>

      <div className="space-y-2">
        {lessons.map((l, i) => (
          <div key={l.id} className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-4 flex items-center gap-3" data-testid={`lesson-row-${l.slug}`}>
            <span className="font-display text-2xl text-gold w-10 text-center flex-shrink-0">{String(l.order).padStart(2,'0')}</span>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-[0.18em] uppercase text-muted-kc">{l.subtitle}</div>
              <div className="font-semibold truncate">{l.title}</div>
              <div className="text-xs text-muted-kc mt-0.5 flex flex-wrap gap-2">
                <span className="font-mono">/{l.slug}</span>
                {l.is_free_preview ? <span className="inline-flex items-center gap-1 text-gold"><Eye size={11}/> Free preview</span> : <span className="inline-flex items-center gap-1"><Lock size={11}/> Locked</span>}
                <span>{l.reading_time_min || 6} min</span>
                <span>{(l.key_takeaways || []).length} takeaways</span>
                <span>{(l.quiz || []).length} quiz Qs</span>
              </div>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <button onClick={() => move(l, 'up')} disabled={i === 0} className="p-2 hover:bg-surface-2 rounded-[var(--kc-radius-sm)] disabled:opacity-30" title="Move up" data-testid={`lm-up-${l.slug}`}><ChevronUp size={16}/></button>
              <button onClick={() => move(l, 'down')} disabled={i === lessons.length - 1} className="p-2 hover:bg-surface-2 rounded-[var(--kc-radius-sm)] disabled:opacity-30" title="Move down" data-testid={`lm-down-${l.slug}`}><ChevronDown size={16}/></button>
              <button onClick={() => setEditing(l)} className="p-2 hover:bg-surface-2 rounded-[var(--kc-radius-sm)]" title="Edit" data-testid={`lm-edit-${l.slug}`}><Edit2 size={16}/></button>
              <button onClick={() => del(l)} className="p-2 hover:bg-red-50 text-[color:var(--kc-danger)] rounded-[var(--kc-radius-sm)]" title="Delete" data-testid={`lm-del-${l.slug}`}><Trash2 size={16}/></button>
            </div>
          </div>
        ))}
        {lessons.length === 0 && <div className="text-center py-12 bg-surface-2 rounded-[var(--kc-radius-md)] text-muted-kc">No lessons yet. Create your first one.</div>}
      </div>

      {editing && <LessonEditor initial={editing} course={course} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function LessonEditor({ initial, course, onClose, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({
    ...EMPTY_LESSON,
    ...initial,
    key_takeaways: initial.key_takeaways || [],
    quiz: initial.quiz || [],
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [takeawayInput, setTakeawayInput] = useState('');

  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const payload = { ...form };
      // Auto-generate slug if missing
      if (!payload.slug) payload.slug = (payload.title || 'lesson').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      if (isNew) {
        await api.post('/admin/lessons', payload);
      } else {
        const { course_id, ...updateFields } = payload;
        await api.put(`/admin/lessons/${initial.id}`, updateFields);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const addTakeaway = () => {
    if (!takeawayInput.trim()) return;
    set('key_takeaways', [...form.key_takeaways, takeawayInput.trim()]);
    setTakeawayInput('');
  };
  const rmTakeaway = (i) => set('key_takeaways', form.key_takeaways.filter((_, idx) => idx !== i));

  const addQuiz = () => set('quiz', [...form.quiz, { q: '', options: ['', '', '', ''], correct: 0 }]);
  const updateQuiz = (qi, key, val) => {
    const q = [...form.quiz];
    q[qi] = { ...q[qi], [key]: val };
    set('quiz', q);
  };
  const updateQuizOption = (qi, oi, val) => {
    const q = [...form.quiz];
    const opts = [...q[qi].options];
    opts[oi] = val;
    q[qi] = { ...q[qi], options: opts };
    set('quiz', q);
  };
  const rmQuiz = (qi) => set('quiz', form.quiz.filter((_, i) => i !== qi));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="lesson-editor-modal">
      <div className="bg-white rounded-[var(--kc-radius-md)] w-full max-w-3xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-kc sticky top-0 bg-white rounded-t-[var(--kc-radius-md)] z-10">
          <div className="font-display uppercase text-2xl">{isNew ? 'New Lesson' : 'Edit Lesson'}</div>
          <button onClick={onClose} className="p-1 text-muted-kc" data-testid="le-close"><X size={20}/></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Order</label>
              <input type="number" value={form.order} onChange={(e) => set('order', parseInt(e.target.value) || 0)} className="kc-input2" data-testid="le-order" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Slug</label>
              <input value={form.slug} onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} placeholder="auto-from-title" className="kc-input2" data-testid="le-slug" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <label className="block text-xs font-semibold uppercase tracking-wider">Title</label>
              <AiGenerateButton
                page="lesson"
                field="title"
                currentValue={form.title || ''}
                onApply={(text) => set('title', text)}
                length="short"
                extraContext={{ subtitle: form.subtitle, course: course?.title }}
              />
            </div>
            <input value={form.title} onChange={(e) => set('title', e.target.value)} className="kc-input2" data-testid="le-title" />
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <label className="block text-xs font-semibold uppercase tracking-wider">Subtitle / Chapter label</label>
              <AiGenerateButton
                page="lesson"
                field="subtitle"
                currentValue={form.subtitle || ''}
                onApply={(text) => set('subtitle', text)}
                length="short"
                extraContext={{ title: form.title, course: course?.title }}
              />
            </div>
            <input value={form.subtitle || ''} onChange={(e) => set('subtitle', e.target.value)} placeholder="Chapter 1" className="kc-input2" data-testid="le-subtitle" />
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <label className="block text-xs font-semibold uppercase tracking-wider">Body (paragraphs separated by blank lines)</label>
              <AiGenerateButton
                page="lesson"
                field="body"
                currentValue={form.body || ''}
                onApply={(text) => set('body', text)}
                length="long"
                extraContext={{ title: form.title, subtitle: form.subtitle, course: course?.title }}
              />
            </div>
            <textarea rows={16} value={form.body} onChange={(e) => set('body', e.target.value)} placeholder="The full lesson text…" className="kc-input2 font-reader text-base leading-relaxed" data-testid="le-body" />
            <div className="text-xs text-muted-kc mt-1">{form.body.split(/\s+/).filter(Boolean).length} words · ~{Math.max(3, Math.floor(form.body.split(/\s+/).filter(Boolean).length / 200))} min read</div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Key Takeaways</label>
            <div className="space-y-2">
              {form.key_takeaways.map((t, i) => (
                <div key={i} className="flex gap-2 items-center bg-surface-2 px-3 py-2 rounded-[var(--kc-radius-sm)] text-sm">
                  <span className="font-display text-gold w-6 text-center">{i+1}</span>
                  <span className="flex-1">{t}</span>
                  <button onClick={() => rmTakeaway(i)} className="text-[color:var(--kc-danger)]"><X size={14}/></button>
                </div>
              ))}
              <div className="flex gap-2">
                <input value={takeawayInput} onChange={(e) => setTakeawayInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTakeaway(); } }} placeholder="Add a key takeaway…" className="kc-input2 flex-1" data-testid="le-takeaway-input" />
                <button onClick={addTakeaway} className="h-11 px-4 rounded-[var(--kc-radius-sm)] bg-ink text-white text-sm font-semibold" data-testid="le-takeaway-add">Add</button>
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <label className="block text-xs font-semibold uppercase tracking-wider">Reflection prompt</label>
              <AiGenerateButton
                page="lesson"
                field="reflection_prompt"
                currentValue={form.reflection_prompt || ''}
                onApply={(text) => set('reflection_prompt', text)}
                length="short"
                extraContext={{ title: form.title, key_takeaways: form.key_takeaways }}
              />
            </div>
            <textarea rows={2} value={form.reflection_prompt || ''} onChange={(e) => set('reflection_prompt', e.target.value)} placeholder="Ask a thoughtful question for students to journal…" className="kc-input2" data-testid="le-reflection" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold uppercase tracking-wider">Quiz Questions</label>
              <button onClick={addQuiz} className="text-xs font-semibold inline-flex items-center gap-1 text-gold" data-testid="le-add-quiz"><Plus size={12}/> Add question</button>
            </div>
            <div className="space-y-3">
              {form.quiz.map((q, qi) => (
                <div key={qi} className="bg-surface-2 p-3 rounded-[var(--kc-radius-sm)]">
                  <div className="flex gap-2 items-start">
                    <span className="font-display text-gold w-6 text-center">{qi+1}</span>
                    <input value={q.q} onChange={(e) => updateQuiz(qi, 'q', e.target.value)} placeholder="Question…" className="kc-input2 flex-1" data-testid={`le-quiz-q-${qi}`} />
                    <button onClick={() => rmQuiz(qi)} className="text-[color:var(--kc-danger)] p-2"><X size={14}/></button>
                  </div>
                  <div className="mt-2 space-y-2 pl-8">
                    {q.options.map((opt, oi) => (
                      <div key={oi} className="flex gap-2 items-center">
                        <input type="radio" name={`correct-${qi}`} checked={q.correct === oi} onChange={() => updateQuiz(qi, 'correct', oi)} className="accent-[color:var(--kc-gold)]" data-testid={`le-quiz-correct-${qi}-${oi}`}/>
                        <input value={opt} onChange={(e) => updateQuizOption(qi, oi, e.target.value)} placeholder={`Option ${oi+1}`} className="kc-input2 flex-1 text-sm" data-testid={`le-quiz-opt-${qi}-${oi}`}/>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Lesson narration audio</label>
            <AudioUploader
              lessonId={initial.id}
              audioUrl={form.audio_url}
              filename={form.audio_filename}
              onChange={(u, n) => setForm((f) => ({ ...f, audio_url: u, audio_filename: n }))}
            />
          </div>

          {/* ===== Cinematic Read Mode ===== */}
          <div className="border-t border-kc pt-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-1.5">
                  <Sparkles size={12} className="text-[color:var(--kc-gold)]"/> Cinematic Read Mode
                </label>
                <p className="text-xs text-muted-kc mt-1">AI background art + ambient mood score for this chapter. Users see a full-screen immersive reader.</p>
              </div>
            </div>

            <div className="mt-3 grid md:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-muted-kc mb-1.5">Mood</label>
                <select
                  value={form.cinematic_mood || 'focus'}
                  onChange={(e) => set('cinematic_mood', e.target.value)}
                  className="kc-input2"
                  data-testid="le-cinematic-mood"
                >
                  {CINEMATIC_MOOD_OPTIONS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <div className={`mt-2 h-8 rounded-[var(--kc-radius-sm)] bg-gradient-to-r ${CINEMATIC_MOOD_OPTIONS.find((o) => o.value === (form.cinematic_mood || 'focus'))?.swatch || ''} border border-kc`} aria-hidden/>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wider text-muted-kc mb-1.5">Background art</label>
                <CinematicArtField
                  lessonId={initial.id}
                  artUrl={form.cinematic_art_url}
                  mood={form.cinematic_mood || 'focus'}
                  onChange={(url) => set('cinematic_art_url', url)}
                />
              </div>
            </div>
          </div>

          <div>
            <button type="button" onClick={() => set('is_free_preview', !form.is_free_preview)} className="flex items-center gap-3 text-sm font-semibold" data-testid="le-free-preview">
              <span className={`relative w-11 h-6 rounded-full transition-colors ${form.is_free_preview ? 'bg-[color:var(--kc-gold)]' : 'bg-gray-300'}`}>
                <span className={`absolute top-0.5 ${form.is_free_preview ? 'left-6' : 'left-0.5'} w-5 h-5 bg-white rounded-full shadow transition-all`} />
              </span>
              {form.is_free_preview ? 'Free preview — available to everyone' : 'Locked — requires unlock'}
            </button>
          </div>
          {err && <div className="text-sm text-[color:var(--kc-danger)]" data-testid="le-error">{err}</div>}
        </div>
        <div className="p-5 border-t border-kc flex justify-end gap-2 sticky bottom-0 bg-white rounded-b-[var(--kc-radius-md)]">
          <button onClick={onClose} className="h-11 px-4 rounded-[var(--kc-radius-sm)] hover:bg-surface-2 transition-colors" data-testid="le-cancel">Cancel</button>
          <button onClick={save} disabled={saving || !form.title || !form.body} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] disabled:opacity-50 transition-colors" data-testid="le-save">
            <Save size={16}/> {saving ? 'Saving…' : 'Save lesson'}
          </button>
        </div>
      </div>
      <style>{`.kc-input2 { width: 100%; padding: 0.625rem 0.75rem; border: 1px solid var(--kc-border-strong); border-radius: var(--kc-radius-sm); font-size: 0.95rem; outline: none; }
      .kc-input2:focus { border-color: var(--kc-gold); box-shadow: 0 0 0 2px var(--kc-gold-soft); }`}</style>
    </div>
  );
}


/* =================== AUDIO UPLOADER =================== */
function AudioUploader({ lessonId, audioUrl, filename, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const hasAudio = !!audioUrl;
  const fmtMB = (b) => (b / (1024 * 1024)).toFixed(1);

  const upload = async (file) => {
    if (!lessonId) {
      setError('Save the lesson first, then upload audio.');
      return;
    }
    setError('');
    if (file.size > 60 * 1024 * 1024) {
      setError(`File is ${fmtMB(file.size)} MB. Max is 60 MB.`);
      return;
    }
    setUploading(true); setProgress(0);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post(`/admin/lessons/${lessonId}/audio`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      onChange(r.data.audio_url, r.data.audio_filename);
      toast.success('Audio uploaded.', { description: `${file.name} · ${fmtMB(r.data.size)} MB` });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Upload failed.');
      toast.error('Upload failed.', { description: String(e?.response?.data?.detail || e.message) });
    } finally {
      setUploading(false); setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const remove = async () => {
    if (!window.confirm('Remove the uploaded audio for this lesson? Listeners will fall back to the device text-to-speech reader.')) return;
    try {
      await api.delete(`/admin/lessons/${lessonId}/audio`);
      onChange('', '');
      toast.success('Audio removed.');
    } catch (e) {
      toast.error('Remove failed.', { description: String(e?.response?.data?.detail || e.message) });
    }
  };

  const audioSrc = audioUrl?.startsWith('http')
    ? audioUrl
    : `${process.env.REACT_APP_BACKEND_URL || ''}${audioUrl || ''}`;

  return (
    <div className="space-y-2" data-testid="audio-uploader">
      {hasAudio ? (
        <div className="p-4 border border-[color:var(--kc-gold)] bg-gold-soft rounded-[var(--kc-radius-md)]" data-testid="audio-uploader-current">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-ink flex items-center justify-center flex-shrink-0">
              <Headphones size={16} className="text-[color:var(--kc-gold)]"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{filename || 'Lesson audio'}</div>
              <div className="text-xs text-muted-kc">Uploaded · plays instead of device TTS</div>
            </div>
            <button onClick={remove} className="h-9 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-[color:var(--kc-danger)] text-xs hover:bg-red-50" data-testid="audio-uploader-remove"><Trash size={12}/> Remove</button>
          </div>
          <audio controls preload="none" src={audioSrc} className="mt-3 w-full" data-testid="audio-uploader-preview"/>
          <div className="mt-3 flex justify-center">
            <button onClick={() => inputRef.current?.click()} className="text-xs font-semibold text-ink underline hover:text-[color:var(--kc-gold-2)]" data-testid="audio-uploader-replace">Replace with a new file</button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="p-6 border-2 border-dashed border-kc-strong rounded-[var(--kc-radius-md)] text-center hover:bg-surface-2 transition-colors cursor-pointer"
          onClick={() => !uploading && inputRef.current?.click()}
          data-testid="audio-uploader-drop"
        >
          {uploading ? (
            <div className="py-3">
              <Loader2 className="mx-auto mb-2 animate-spin text-[color:var(--kc-gold-2)]" size={24}/>
              <div className="text-sm font-semibold">Uploading… {progress}%</div>
              <div className="mt-2 h-1.5 w-full bg-white rounded-full overflow-hidden border border-kc">
                <div className="h-full bg-[color:var(--kc-gold)] transition-all" style={{ width: `${progress}%` }}/>
              </div>
            </div>
          ) : (
            <>
              <Mic className="mx-auto mb-2 text-muted-kc" size={24}/>
              <div className="text-sm font-semibold">Upload your recorded audio</div>
              <div className="text-xs text-muted-kc mt-1">Drag & drop or click to pick · MP3, M4A, WAV, OGG · max 60 MB</div>
              <div className="text-[10px] text-muted-kc mt-2 uppercase tracking-wider">No upload = device TTS fallback</div>
            </>
          )}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="audio/mpeg,audio/mp4,audio/wav,audio/ogg,audio/webm,audio/aac,audio/flac,audio/*,.mp3,.m4a,.wav,.ogg,.webm,.aac,.flac"
        className="hidden"
        onChange={onPick}
        data-testid="audio-uploader-input"
      />
      {error && <div className="text-xs text-[color:var(--kc-danger)]" data-testid="audio-uploader-error">{error}</div>}
      {!lessonId && <div className="text-xs text-muted-kc">Save the lesson once before uploading audio.</div>}
    </div>
  );
}



/* =================== CINEMATIC ART FIELD =================== */
function CinematicArtField({ lessonId, artUrl, mood, onChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  const fullUrl = artUrl?.startsWith('http')
    ? artUrl
    : artUrl
      ? `${process.env.REACT_APP_BACKEND_URL || ''}${artUrl}`
      : '';

  const generate = async () => {
    if (!lessonId) {
      setErr('Save the lesson first.');
      return;
    }
    setBusy(true); setErr('');
    try {
      const { data } = await api.post(`/admin/lessons/${lessonId}/cinematic-art/generate`, { mood });
      onChange(data.art_url);
      toast.success('Cinematic art generated.');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Generation failed.');
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purpose', 'lesson_cinematic');
      const { data } = await api.post('/admin/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(data.url);
      toast.success('Custom art uploaded.');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="cinematic-art-field">
      {fullUrl ? (
        <div className="relative rounded-[var(--kc-radius-sm)] overflow-hidden border border-kc bg-black">
          <img src={fullUrl} alt="Cinematic background" className="w-full aspect-video object-cover" data-testid="cinematic-art-preview"/>
          <button type="button" onClick={() => onChange('')} className="absolute top-2 right-2 h-8 w-8 inline-flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black" aria-label="Remove art" data-testid="cinematic-art-remove">
            <X size={14}/>
          </button>
        </div>
      ) : (
        <div className="aspect-video rounded-[var(--kc-radius-sm)] border border-dashed border-kc-strong flex items-center justify-center text-xs text-muted-kc bg-surface-2">
          No art yet — generate or upload below.
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={generate}
          disabled={busy || !lessonId}
          className="flex-1 h-10 inline-flex items-center justify-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white text-xs font-semibold hover:bg-black/90 disabled:opacity-40 transition-colors"
          data-testid="cinematic-art-generate"
        >
          {busy ? <RefreshCw size={12} className="animate-spin"/> : <Sparkles size={12} className="text-[color:var(--kc-gold)]"/>}
          {busy ? 'Generating…' : (fullUrl ? 'Regenerate art' : 'Generate AI art')}
        </button>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="h-10 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] border border-kc-strong text-xs font-semibold hover:bg-surface-2 transition-colors disabled:opacity-40"
          data-testid="cinematic-art-upload"
        >
          <Upload size={12}/> Upload
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="cinematic-art-file"
        />
      </div>
      {err && <div className="text-xs text-[color:var(--kc-danger)]" data-testid="cinematic-art-error">{err}</div>}
      {!lessonId && <div className="text-[11px] text-muted-kc">Save the lesson once before generating art.</div>}
    </div>
  );
}
